import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck } from 'lucide-react';
import { formatCurrency } from '@shared/formatCurrency';
import { SheetModal } from '@/components/SheetModal';
import { ROISlideOutContent } from '../LearnMoreSlideOutContent';
import { FundingPolicyType } from '@shared';

interface QuoteROIDisplayProps {
  projectedCostCoverage?: number;
  policyType?: number;
  displayROI?: string;
  className?: string;
}

/**
 * Reusable component for displaying projected coverage and ROI information
 * Used in quote cards to show the financial benefits of a quote
 */
export function QuoteROIDisplay({
  projectedCostCoverage,
  policyType,
  displayROI,
  className = 'mr-8',
}: QuoteROIDisplayProps) {
  const [isROIGuidanceOpen, setIsROIGuidanceOpen] = useState(false);
  // Only render if we have projected cost coverage data
  if (projectedCostCoverage === undefined) {
    return null;
  }

  const totalValue = projectedCostCoverage;
  const isCareAssurance = policyType === FundingPolicyType.careScoutCareAssurance;

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <div className="text-primary-dark text-sm font-medium">Projected Coverage</div>
      <button
        type="button"
        onClick={e => {
          e.stopPropagation();
          setIsROIGuidanceOpen(true);
        }}
        className="flex items-center gap-1 text-base font-semibold text-green-600 underline decoration-dotted decoration-1 underline-offset-2 transition-colors hover:text-green-700 sm:text-lg"
      >
        <ShieldCheck
          className="size-[18px]"
          strokeWidth={2.5}
        />
        {formatCurrency(totalValue)}
      </button>
      {displayROI && (
        <Badge className="w-max rounded-lg bg-green-400/20 px-2 py-1 text-green-600 hover:bg-green-400/20">
          ROI {displayROI}
          {isCareAssurance && <sup>*</sup>}
        </Badge>
      )}

      <SheetModal
        open={isROIGuidanceOpen}
        onClose={() => setIsROIGuidanceOpen(false)}
        title="Waterlily AI"
        subTitle=""
        modal={true}
        className="min-w-[95%] sm:min-w-[90%] md:min-w-[600px] lg:min-w-[650px]"
      >
        <div className="flex min-h-full flex-1 flex-col overflow-auto text-black">
          <div className="flex flex-1 flex-col py-4">
            <ROISlideOutContent policyType={policyType} />
          </div>
        </div>
      </SheetModal>
    </div>
  );
}
